/** A coluna do assistente: cabeçalho enxuto, mensagens e campo. Cabeçalho com
 *  o título Assistente e, abaixo, o título da conversa, gerado pelo modelo e
 *  editável com um clique; à direita, histórico, nova conversa, o menu de
 *  opções e fechar. O menu, de 07/09/2026, guarda o que não precisa de botão
 *  próprio: mostrar ou esconder etapas e consultas, as conversas arquivadas e
 *  apagar o histórico inteiro.
 *
 *  Região complementar com rótulo; abrir move o foco para o campo, Esc fecha e
 *  devolve o foco ao botão, e a resposta é anunciada ao leitor de tela quando
 *  termina, não a cada trecho. */
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Archive, Check, Database, History, ListChecks, MoreVertical, Plus, Trash2, X } from 'lucide-react'
import { useI18n } from '@/core/i18n/useI18n'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { layout } from '@/styles/theme'
import './assistente.css'
import { useApagarTodas, useRenomearConversa } from './api'
import { useAssistente, useConversaDoAssistente } from './AssistenteProvider'
import { Composer } from './Composer'
import { ConversaLista } from './ConversaLista'
import { MensagemLista } from './MensagemLista'

/** Medidas em px por extenso: no `sx` do MUI, `width: 1` quer dizer 100%, e o
 *  bloco esticava a coluna inteira em vez de sumir num pixel. */
const escondidoVisualmente = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const

function TituloDaConversa() {
  const { t } = useI18n()
  const { conversaAtual, titulo } = useConversaDoAssistente()
  const renomear = useRenomearConversa()
  const [editando, setEditando] = useState(false)
  const [rascunho, setRascunho] = useState('')

  // Trocar de conversa fecha a edição que ficou aberta.
  useEffect(() => {
    setEditando(false)
  }, [conversaAtual])

  if (!conversaAtual) {
    return <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{t('assistente.conversa.nova')}</Typography>
  }

  function comecar() {
    setRascunho(titulo ?? '')
    setEditando(true)
  }
  function salvar() {
    setEditando(false)
    const novo = rascunho.trim()
    if (!novo || novo === (titulo ?? '')) return
    renomear.mutate({ uuid: conversaAtual as string, titulo: novo })
  }
  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      salvar()
    } else if (e.key === 'Escape') {
      // Só cancela a edição; o painel não fecha por este Esc.
      e.stopPropagation()
      setEditando(false)
    }
  }

  if (editando) {
    return (
      <TextField
        size="small"
        autoFocus
        fullWidth
        value={rascunho}
        onChange={(e) => setRascunho(e.target.value)}
        onBlur={salvar}
        onKeyDown={aoTeclar}
        slotProps={{ htmlInput: { 'aria-label': t('assistente.conversa.tituloRotulo'), 'data-assistente-composer': 'true', maxLength: 120 } }}
        sx={{ mt: 0.25, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 } }}
      />
    )
  }

  return (
    <Tooltip title={t('assistente.conversa.editarTitulo')} enterDelay={600}>
      <ButtonBase
        onClick={comecar}
        aria-label={t('assistente.conversa.editarTitulo')}
        sx={{
          display: 'block',
          maxWidth: '100%',
          textAlign: 'left',
          borderRadius: 1,
          px: 0.5,
          mx: -0.5,
          '&:hover': { bgcolor: 'action.hover' },
          '&.Mui-focusVisible': { boxShadow: 'var(--focus-ring)' },
        }}
      >
        <Typography noWrap sx={{ fontSize: '0.75rem', color: titulo ? 'text.secondary' : 'text.disabled' }}>
          {titulo ?? t('assistente.conversa.semTitulo')}
        </Typography>
      </ButtonBase>
    </Tooltip>
  )
}

/** O menu de opções do cabeçalho. Os dois interruptores mostram um traço de
 *  marcado quando ligados; as duas ações de baixo levam ao histórico de
 *  arquivadas e ao Limpar histórico, que pede confirmação. */
function MenuDeOpcoes() {
  const { t } = useI18n()
  const {
    setVista, setAbaDoHistorico, mostrarEtapas, setMostrarEtapas,
    podeVerConsultas, mostrarConsultas, setMostrarConsultas,
  } = useAssistente()
  const { novaConversa } = useConversaDoAssistente()
  const apagarTodas = useApagarTodas()
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  function abrirArquivadas() {
    setMenuEl(null)
    setAbaDoHistorico('arquivadas')
    setVista('historico')
  }
  function confirmarApagarTudo() {
    apagarTodas.mutate(undefined, {
      onSuccess: () => {
        setConfirmando(false)
        novaConversa()
      },
      onError: () => setConfirmando(false),
    })
  }

  const marca = (ligado: boolean) => (
    <ListItemIcon sx={{ color: ligado ? 'primary.main' : 'transparent' }}>
      <Check size={15} strokeWidth={2.4} aria-hidden />
    </ListItemIcon>
  )

  return (
    <>
      <Tooltip title={t('assistente.painel.mais')}>
        <IconButton
          size="small"
          onClick={(e: MouseEvent<HTMLButtonElement>) => setMenuEl(e.currentTarget)}
          aria-label={t('assistente.painel.mais')}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuEl)}
          sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
        >
          <MoreVertical size={18} strokeWidth={2} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={() => setMenuEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem dense role="menuitemcheckbox" aria-checked={mostrarEtapas} onClick={() => setMostrarEtapas(!mostrarEtapas)}>
          {marca(mostrarEtapas)}
          <ListChecks size={15} aria-hidden style={{ marginRight: 8 }} />
          {t('assistente.opcoes.mostrarEtapas')}
        </MenuItem>
        {podeVerConsultas && (
          <MenuItem dense role="menuitemcheckbox" aria-checked={mostrarConsultas} onClick={() => setMostrarConsultas(!mostrarConsultas)}>
            {marca(mostrarConsultas)}
            <Database size={15} aria-hidden style={{ marginRight: 8 }} />
            {t('assistente.opcoes.mostrarConsultas')}
          </MenuItem>
        )}
        <Divider />
        <MenuItem dense onClick={abrirArquivadas}>
          <ListItemIcon><Archive size={15} /></ListItemIcon>
          {t('assistente.painel.arquivadas')}
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            setMenuEl(null)
            setConfirmando(true)
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><Trash2 size={15} /></ListItemIcon>
          {t('assistente.opcoes.apagarTudo')}
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={confirmando}
        title={t('assistente.opcoes.apagarTudoTitulo')}
        message={t('assistente.opcoes.apagarTudoTexto')}
        confirmLabel={t('assistente.opcoes.apagarTudo')}
        danger
        loading={apagarTodas.isPending}
        onConfirm={confirmarApagarTudo}
        onClose={() => setConfirmando(false)}
      />
    </>
  )
}

export function AssistentePanel() {
  const { t } = useI18n()
  const { vista, setVista, fechar, botaoRef, abaDoHistorico, setAbaDoHistorico } = useAssistente()
  const { fluxo, novaConversa } = useConversaDoAssistente()
  const raizRef = useRef<HTMLDivElement | null>(null)
  const [anuncio, setAnuncio] = useState('')
  const statusAnteriorRef = useRef(fluxo.status)

  // Anuncia a resposta inteira quando termina, nunca trecho por trecho.
  useEffect(() => {
    if (statusAnteriorRef.current !== 'concluido' && fluxo.status === 'concluido') {
      setAnuncio(`${t('assistente.resposta.anuncio')} ${fluxo.texto}`)
    }
    statusAnteriorRef.current = fluxo.status
  }, [fluxo.status, fluxo.texto, t])

  function fecharEDevolverFoco() {
    fechar()
    botaoRef.current?.focus()
  }

  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Escape' || e.defaultPrevented) return
    // Só o que está DENTRO do painel: menus e diálogos em portal cuidam do próprio Esc.
    if (!(e.target instanceof Node) || !raizRef.current?.contains(e.target)) return
    e.preventDefault()
    fecharEDevolverFoco()
  }

  const iconeSx = { color: 'text.secondary', '&:hover': { color: 'text.primary' } }
  const noHistorico = vista === 'historico'

  return (
    <Box
      ref={raizRef}
      onKeyDown={aoTeclar}
      className="anim-fade-up"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minWidth: 0, bgcolor: 'background.paper' }}
    >
      <Box
        sx={{
          minHeight: layout.topbarHeight,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flex: 'none',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          <Typography component="h2" sx={{ fontFamily: '"Fredoka", sans-serif', fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.2, color: 'text.primary' }}>
            {t('assistente.painel.titulo')}
          </Typography>
          {noHistorico
            ? (
              <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {t(abaDoHistorico === 'arquivadas' ? 'assistente.painel.arquivadas' : 'assistente.painel.historico')}
              </Typography>
            )
            : <TituloDaConversa />}
        </Box>
        <Tooltip title={t(noHistorico ? 'assistente.painel.voltar' : 'assistente.painel.historico')}>
          <IconButton
            size="small"
            onClick={() => {
              if (!noHistorico) setAbaDoHistorico('ativas')
              setVista(noHistorico ? 'conversa' : 'historico')
            }}
            aria-pressed={noHistorico}
            aria-label={t('assistente.painel.historico')}
            sx={{ ...iconeSx, color: noHistorico ? 'primary.main' : 'text.secondary' }}
          >
            <History size={18} strokeWidth={2} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('assistente.painel.nova')}>
          <IconButton size="small" onClick={novaConversa} aria-label={t('assistente.painel.nova')} sx={iconeSx}>
            <Plus size={18} strokeWidth={2} />
          </IconButton>
        </Tooltip>
        <MenuDeOpcoes />
        <Tooltip title={t('assistente.painel.fechar')}>
          <IconButton size="small" onClick={fecharEDevolverFoco} aria-label={t('assistente.painel.fechar')} sx={iconeSx}>
            <X size={18} strokeWidth={2} />
          </IconButton>
        </Tooltip>
      </Box>

      {noHistorico ? (
        <ConversaLista />
      ) : (
        <>
          <MensagemLista />
          <Composer />
        </>
      )}

      <Box aria-live="polite" aria-atomic="true" sx={escondidoVisualmente}>{anuncio}</Box>
    </Box>
  )
}
